import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileSpreadsheet } from "lucide-react";

function downloadFile(endpoint, params, defaultName) {
  const q = new URLSearchParams(params).toString();
  const url = `${API}${endpoint}?${q}`;
  fetch(url, { credentials: "include" })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      const ext = params.fmt || "xlsx";
      a.href = URL.createObjectURL(blob);
      a.download = `${defaultName}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch((e) => toast.error(e.message));
}

function getPageTitle(role) {
  if (role === "sales") return "My Reports";
  if (role === "operations") return "Operations Reports";
  return "Reports & Exports";
}

function getPageSubtitle(role) {
  if (role === "sales") return "Download your orders and claims as Excel or CSV";
  if (role === "operations") return "Download manual orders by date, BDM or total";
  return "Download data as Excel or CSV";
}

function getOrdersCardTitle(role) {
  if (role === "sales") return "My Orders";
  if (role === "operations") return "Manual Orders (Total)";
  return "All Orders";
}

function getOrdersCardDescription(role) {
  if (role === "sales") return "All your manual and claimed orders.";
  if (role === "operations") return "All manual orders. Apply date filters or leave empty for total.";
  return "Manual + approved claim orders, with all filters applied.";
}

export default function Reports() {
  const { user } = useAuth();
  const role = user.role;

  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [fmt, setFmt] = useState("xlsx");
  const [summary, setSummary] = useState(null);

  const isAdminLike = role === "admin" || role === "subadmin";
  const canPickAgent = isAdminLike || role === "operations";
  const canSeeClaims = isAdminLike || role === "sales";
  const canSeeApprovalSummary = isAdminLike;

  const loadMeta = useCallback(async () => {
    try {
      const promises = [];
      if (canPickAgent) promises.push(api.get("/users/agents"));
      if (canSeeApprovalSummary) promises.push(api.get("/reports/approval-summary"));
      const results = await Promise.all(promises);
      let idx = 0;
      if (canPickAgent) { setAgents(results[idx].data); idx += 1; }
      if (canSeeApprovalSummary) { setSummary(results[idx].data); }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [canPickAgent, canSeeApprovalSummary]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const params = (extra = {}) => {
    const p = { fmt, ...extra };
    if (canPickAgent && agent) p.agent_id = agent;
    if (start) p.start_date = start;
    if (end) p.end_date = end;
    return p;
  };

  const title = getPageTitle(role);
  const subtitle = getPageSubtitle(role);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>

      <Card className="p-4 md:p-6 space-y-4">
        <h2 className="font-display font-semibold text-lg">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {canPickAgent && (
            <div className="space-y-2">
              <Label>BDM</Label>
              <Select value={agent} onValueChange={(v) => setAgent(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="report-agent"><SelectValue placeholder="All BDMs" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All BDMs</SelectItem>
                  {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label>Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
          </div>
          <div className="space-y-2">
            <Label>End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
          </div>
          <div className="space-y-2">
            <Label>Format</Label>
            <Select value={fmt} onValueChange={setFmt}>
              <SelectTrigger data-testid="report-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 md:p-6 space-y-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-display font-semibold">{getOrdersCardTitle(role)}</h3>
          </div>
          <p className="text-sm text-muted-foreground">{getOrdersCardDescription(role)}</p>
          <Button onClick={() => downloadFile("/reports/orders", params(), role === "sales" ? "my_orders" : "all_orders")} data-testid="download-all-orders">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        </Card>

        {canPickAgent && (
          <Card className="p-4 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-display font-semibold">BDM-wise Orders</h3>
            </div>
            <p className="text-sm text-muted-foreground">Select a BDM above. Combines with date filters.</p>
            <Button disabled={!agent} onClick={() => downloadFile("/reports/orders", params(), "agent_orders")} data-testid="download-agent-orders">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </Card>
        )}

        {canSeeClaims && (
          <Card className="p-4 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-display font-semibold">
                {role === "sales" ? "My Claim Orders" : "BDM-wise Claim Orders"}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {role === "sales" ? "All your submitted claims." : "Select a BDM above to export their claims."}
            </p>
            <Button
              disabled={role !== "sales" && !agent}
              onClick={() => downloadFile("/reports/claims", params(), role === "sales" ? "my_claims" : "agent_claims")}
              data-testid="download-agent-claims"
            >
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </Card>
        )}

        {canSeeApprovalSummary && (
          <Card className="p-4 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-display font-semibold">Approval Team Report</h3>
            </div>
            {summary && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-medium">{summary.total}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Pending</span><span className="font-medium">{summary.pending}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Approved</span><span className="font-medium">{summary.approved}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Rejected</span><span className="font-medium">{summary.rejected}</span></div>
              </div>
            )}
            <Button onClick={() => downloadFile("/reports/claims", params(), "all_claims")} data-testid="download-all-claims">
              <Download className="w-4 h-4 mr-2" /> Download all claims
            </Button>
          </Card>
        )}

        {role === "admin" && (
          <Card className="p-4 md:p-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
              <h3 className="font-display font-semibold">B2B Billing Report</h3>
            </div>
            <p className="text-sm text-muted-foreground">All approved + invoice-uploaded B2B submissions, one row per line item (with invoice number & amount).</p>
            <Button onClick={() => downloadFile("/reports/b2b", { fmt, ...(start ? { start_date: start } : {}), ...(end ? { end_date: end } : {}) }, "b2b_report")} data-testid="download-b2b-report">
              <Download className="w-4 h-4 mr-2" /> Download B2B report
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
